import React, { useEffect, useState } from "react";
import { Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/** DSP-1 — a foreman raises a Quick Dispatch request from the field; it lands on top of the dispatch pool. */
export default function QuickDispatchButton({ projectId }) {
  const [enabled, setEnabled] = useState(false);
  const [scopes, setScopes] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ scope: "", what: "", estimated_hours: 2, needed_by: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/app").then((r) => setEnabled(!!r.data?.quick_dispatch_requests)).catch(() => {});
    api.get(`/projects/${projectId}/scopes`).then((r) => setScopes([...new Set((r.data?.scopes || []).map((s) => s.scope_code || s.scope_type).filter(Boolean))])).catch(() => {});
  }, [projectId]);
  if (!enabled) return null;
  const submit = async () => {
    if (!f.what.trim()) { toast.error("Say what is needed"); return; }
    setBusy(true);
    try {
      await api.post("/dispatch/quick-requests", { project_id: projectId, ...f, estimated_hours: Number(f.estimated_hours) || 2 });
      toast.success("Quick request sent to Dispatch");
      setOpen(false); setF({ scope: "", what: "", estimated_hours: 2, needed_by: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Request failed"); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setOpen(true)} data-testid="quick-dispatch-btn" title="Ask Dispatch for a small crew today or tomorrow">
        <Zap className="w-3.5 h-3.5 mr-1 text-amber-600" /> Quick dispatch
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="quick-dispatch-dialog">
          <DialogHeader><DialogTitle className="text-base">Quick dispatch request</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <label className="block text-xs text-muted-foreground">Scope
              <select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} data-testid="quick-dispatch-scope" className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground">
                <option value="">— any —</option>
                {scopes.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block text-xs text-muted-foreground">What is needed
              <Textarea value={f.what} onChange={(e) => setF({ ...f, what: e.target.value })} rows={3} placeholder="e.g. 2 installers to finish TRA on floor 2, material on site" data-testid="quick-dispatch-what" className="mt-1" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs text-muted-foreground">Estimated hours
                <Input type="number" min="0.5" step="0.5" value={f.estimated_hours} onChange={(e) => setF({ ...f, estimated_hours: e.target.value })} data-testid="quick-dispatch-hours" className="mt-1" />
              </label>
              <label className="block text-xs text-muted-foreground">Needed by
                <Input type="date" value={f.needed_by} onChange={(e) => setF({ ...f, needed_by: e.target.value })} data-testid="quick-dispatch-needed-by" className="mt-1" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="quick-dispatch-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="quick-dispatch-submit">{busy ? "Sending…" : "Send to Dispatch"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
